import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { notificationsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose, onToggle }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState<'all' | 'unread' | 'critical'>('all');
  const [sourceFilter, setSourceFilter] = React.useState<'all' | 'starlink-monitor' | 'powerbi-monitor' | 'xero-monitor' | 'impactplan-monitor'>('all');

  const { data, isLoading } = useQuery(['notifications', filter, sourceFilter], () => notificationsAPI.list({
    unread: filter === 'unread',
    severity: filter === 'critical' ? 'critical' : undefined,
    source: sourceFilter === 'all' ? undefined : sourceFilter,
    limit: 100,
  }), {
    refetchInterval: 30000,
  });

  const notifications = data?.data || [];
  const unreadCount = data?.unreadCount || 0;

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'critical' && !(notification.severity === 'critical' || notification.severity === 'error')) return false;
    if (sourceFilter !== 'all' && notification.source !== sourceFilter) return false;
    return true;
  });

  const refreshNotifications = () => queryClient.invalidateQueries('notifications');

  const markAsRead = async (id: number) => {
    await notificationsAPI.markRead(id);
    refreshNotifications();
  };

  const markAllAsRead = async () => {
    await notificationsAPI.markAllRead();
    refreshNotifications();
  };

  const openNotification = async (notification: any) => {
    if (!notification.readAt) {
      await markAsRead(notification.id);
    }
    if (notification.targetUrl) {
      navigate(notification.targetUrl);
      onClose();
    }
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={onToggle}
          className="relative p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg transition-all duration-200"
        >
          <i className="fas fa-bell text-xl"></i>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[20px] h-5">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-25 backdrop-blur-sm" onClick={onClose}></div>
          <div className="absolute top-0 right-0 w-full max-w-md h-full bg-white shadow-2xl">
            <div className="flex h-full flex-col">
              <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">Operational inbox</h3>
                    <p className="text-blue-100 text-sm">{unreadCount} unread</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="rounded-lg bg-white/10 px-3 py-1 text-sm font-medium text-white hover:bg-white/15"
                      >
                        Mark all read
                      </button>
                    )}
                    <button onClick={onClose} className="rounded-lg p-2 text-white hover:bg-white/10">
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'unread', label: 'Unread' },
                    { key: 'critical', label: 'Critical' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setFilter(option.key as 'all' | 'unread' | 'critical')}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                        filter === option.key ? 'bg-white text-slate-900' : 'bg-white/10 text-white hover:bg-white/15'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mt-3">
                  <select
                    value={sourceFilter}
                    onChange={(event) => setSourceFilter(event.target.value as typeof sourceFilter)}
                    className="w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2 text-sm text-white outline-none"
                  >
                    <option value="all" className="text-slate-900">All sources</option>
                    <option value="starlink-monitor" className="text-slate-900">Starlink</option>
                    <option value="powerbi-monitor" className="text-slate-900">Power BI</option>
                    <option value="xero-monitor" className="text-slate-900">Xero</option>
                    <option value="impactplan-monitor" className="text-slate-900">Impact Plan</option>
                  </select>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {isLoading ? (
                  <div className="py-12 text-center text-gray-500">Loading notifications...</div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">No notifications in this view.</div>
                ) : (
                  filteredNotifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => openNotification(notification)}
                      className={`w-full rounded-2xl border p-4 text-left transition hover:shadow-md ${
                        notification.readAt ? 'border-gray-100 bg-gray-50' : 'border-blue-100 bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">{notification.title}</p>
                          <p className="mt-2 text-sm text-gray-600">{notification.body}</p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-[10px] font-bold uppercase text-gray-500">
                          {notification.severity}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                        <span>{notification.source || 'system'}</span>
                        <span>{new Date(notification.createdAt).toLocaleString()}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCenter;
